<template>
  <div class="organization-setup-wizard">
    <!-- Header -->
    <div class="wizard-header">
      <h1><i class="fas fa-magic"></i> Quick Organization Setup</h1>
      <p class="wizard-subtitle">
        Create a new organization with pre-configured projects, checks, and users based on industry templates.
        Save hours of manual setup time!
      </p>
    </div>

    <!-- Progress Indicator -->
    <div class="progress-indicator">
      <div class="progress-steps">
        <div
          v-for="(step, index) in steps"
          :key="step.id"
          :class="['progress-step', {
            active: currentStep === index,
            completed: index < currentStep,
            upcoming: index > currentStep
          }]"
        >
          <div class="step-circle">
            <i v-if="index < currentStep" class="fas fa-check"></i>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <div class="step-label">{{ step.label }}</div>
        </div>
      </div>
      <div class="progress-bar">
        <div
          class="progress-fill"
          :style="{ width: `${((currentStep + 1) / steps.length) * 100}%` }"
        ></div>
      </div>
    </div>

    <!-- Step Content -->
    <div class="wizard-content">
      <!-- Step 1: Organization Details -->
      <div v-if="currentStep === 0" class="step-content">
        <div class="step-header">
          <h2><i class="fas fa-building"></i> Organization Information</h2>
          <p>Basic information about the new organization</p>
        </div>

        <div class="form-grid">
          <div class="form-group">
            <label>Organization Name *</label>
            <input
              v-model="organizationData.name"
              type="text"
              class="form-control"
              placeholder="ABC Construction Inc."
              required
            >
          </div>

          <div class="form-group">
            <label>Industry *</label>
            <select v-model="organizationData.industry" class="form-control" required>
              <option value="">Select Industry</option>
              <option value="CONSTRUCTION">Construction</option>
              <option value="INSURANCE">Insurance</option>
              <option value="REAL_ESTATE">Real Estate</option>
              <option value="HEALTHCARE">Healthcare</option>
              <option value="TECHNOLOGY">Technology</option>
              <option value="MANUFACTURING">Manufacturing</option>
              <option value="RETAIL">Retail</option>
              <option value="PROFESSIONAL_SERVICES">Professional Services</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <div class="form-group">
            <label>Primary Contact Email *</label>
            <input
              v-model="organizationData.primary_contact_email"
              type="email"
              class="form-control"
              placeholder="admin@company.com"
              required
            >
          </div>

          <div class="form-group">
            <label>Subscription Tier</label>
            <select v-model="organizationData.subscription_tier" class="form-control">
              <option value="STANDARD">Standard</option>
              <option value="PROFESSIONAL">Professional</option>
              <option value="ENTERPRISE">Enterprise</option>
              <option value="TRIAL">Trial</option>
            </select>
          </div>

          <div class="form-group full-width">
            <label>Company Size</label>
            <select v-model="organizationData.company_size" class="form-control">
              <option value="">Select Size</option>
              <option value="1-10">1-10 employees</option>
              <option value="11-50">11-50 employees</option>
              <option value="51-200">51-200 employees</option>
              <option value="201-1000">201-1000 employees</option>
              <option value="1000+">1000+ employees</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Step 2: Template Selection -->
      <div v-if="currentStep === 1" class="step-content">
        <div class="step-header">
          <h2><i class="fas fa-th-large"></i> Choose Industry Template</h2>
          <p>Select a pre-configured template that matches your client's industry</p>
        </div>

        <div v-if="loadingTemplates" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          <p>Loading templates...</p>
        </div>

        <div v-else-if="templates.length === 0" class="empty-state">
          <i class="fas fa-exclamation-triangle"></i>
          <h3>No Templates Available</h3>
          <p>No industry templates are currently available. Please contact support.</p>
        </div>

        <div v-else class="template-grid">
          <div
            v-for="template in filteredTemplates"
            :key="template.id"
            :class="['template-card', { selected: selectedTemplate?.id === template.id }]"
            @click="selectTemplate(template)"
          >
            <div class="template-header">
              <div class="template-icon">
                <i :class="getIndustryIcon(template.industry_type)"></i>
              </div>
              <div class="template-meta">
                <h3>{{ template.name }}</h3>
                <span class="industry-badge">{{ template.industry_display }}</span>
              </div>
            </div>

            <p class="template-description">{{ template.description }}</p>

            <div class="template-stats">
              <div class="stat">
                <i class="fas fa-folder"></i>
                <span>{{ template.project_count }} Projects</span>
              </div>
              <div class="stat">
                <i class="fas fa-clipboard-check"></i>
                <span>{{ template.check_count }} Checks</span>
              </div>
            </div>

            <div v-if="template.is_default" class="default-badge">
              <i class="fas fa-star"></i> Recommended
            </div>
          </div>
        </div>

        <!-- Template Preview -->
        <div v-if="selectedTemplate && templatePreview" class="template-preview">
          <h3>Template Preview: {{ selectedTemplate.name }}</h3>

          <div class="preview-section">
            <h4><i class="fas fa-folder"></i> Projects to Create</h4>
            <div class="preview-list">
              <div v-for="project in templatePreview.projects" :key="project.name" class="preview-item">
                <strong>{{ project.name }}</strong>
                <p>{{ project.description }}</p>
                <small>{{ project.type }} • {{ project.check_types.length }} check types</small>
              </div>
            </div>
          </div>

          <div class="preview-section">
            <h4><i class="fas fa-clipboard-check"></i> Comprehensive Checks</h4>
            <div class="preview-list">
              <div v-for="check in templatePreview.checks.slice(0, 5)" :key="check.name" class="preview-item">
                <strong>{{ check.name }}</strong>
                <small>{{ check.type }}</small>
              </div>
              <div v-if="templatePreview.checks.length > 5" class="preview-item">
                <em>... and {{ templatePreview.checks.length - 5 }} more checks</em>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 3: Customization Options -->
      <div v-if="currentStep === 2" class="step-content">
        <div class="step-header">
          <h2><i class="fas fa-sliders-h"></i> Customize Setup</h2>
          <p>Configure what should be automatically created</p>
        </div>

        <div class="customization-options">
          <div class="option-group">
            <h3>Auto-Creation Settings</h3>

            <div class="option-item">
              <label class="checkbox-label">
                <input
                  v-model="setupOptions.auto_create_projects"
                  type="checkbox"
                  class="checkbox"
                >
                <div class="checkbox-content">
                  <strong>Create Default Projects</strong>
                  <p>Automatically create pre-configured projects for this organization</p>
                </div>
              </label>
            </div>

            <div class="option-item">
              <label class="checkbox-label">
                <input
                  v-model="setupOptions.auto_create_checks"
                  type="checkbox"
                  class="checkbox"
                >
                <div class="checkbox-content">
                  <strong>Assign Comprehensive Checks</strong>
                  <p>Link appropriate comprehensive check definitions to projects</p>
                </div>
              </label>
            </div>

            <div class="option-item">
              <label class="checkbox-label">
                <input
                  v-model="setupOptions.auto_invite_users"
                  type="checkbox"
                  class="checkbox"
                >
                <div class="checkbox-content">
                  <strong>Invite Default Users</strong>
                  <p>Send invitations to recommended team members (security consideration)</p>
                  <small class="warning-text">⚠️ This will create user accounts</small>
                </div>
              </label>
            </div>
          </div>

          <div v-if="setupOptions.auto_invite_users" class="option-group">
            <h3>Default Users</h3>
            <p>Add the email addresses for default users that should be invited:</p>

            <div v-for="(user, index) in setupOptions.default_users" :key="index" class="user-input-row">
              <select v-model="user.role" class="form-control role-select" required>
                <option value="">Select Role</option>
                <option value="ADMIN">Organization Administrator</option>
                <option value="RISK_MANAGER">Risk Manager</option>
                <option value="ANALYST">Risk Analyst</option>
                <option value="CLIENT_MANAGER">Client Manager</option>
                <option value="REVIEWER">Document Reviewer</option>
              </select>
              <input
                v-model="user.email"
                type="email"
                class="form-control"
                placeholder="user@company.com"
                required
              >
              <input
                v-model="user.first_name"
                type="text"
                class="form-control"
                placeholder="First Name"
                required
              >
              <input
                v-model="user.last_name"
                type="text"
                class="form-control"
                placeholder="Last Name"
                required
              >
              <button @click="removeUser(index)" class="btn-remove-user">
                <i class="fas fa-trash"></i>
              </button>
            </div>

            <button @click="addUser" class="btn-add-user">
              <i class="fas fa-plus"></i> Add Another User
            </button>
          </div>

          <div class="option-group">
            <h3>AI Settings</h3>

            <div class="form-group">
              <label>Default LLM Provider</label>
              <select v-model="setupOptions.default_llm_provider" class="form-control">
                <option value="gemini">Gemini (Google) - Recommended</option>
                <option value="openai">OpenAI (GPT-4)</option>
                <option value="claude">Claude (Anthropic)</option>
                <option value="grok">Grok (xAI)</option>
              </select>
            </div>

            <div class="form-group">
              <label>Default LLM Model</label>
              <select v-model="setupOptions.default_llm_model" class="form-control">
                <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                <option value="gpt-4-turbo">GPT-4 Turbo</option>
                <option value="claude-3.5-sonnet">Claude 3.5 Sonnet</option>
                <option value="grok-1">Grok</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 4: Review & Create -->
      <div v-if="currentStep === 3" class="step-content">
        <div class="step-header">
          <h2><i class="fas fa-check-circle"></i> Review & Create</h2>
          <p>Review your selections and create the organization</p>
        </div>

        <div class="review-summary">
          <div class="summary-section">
            <h3><i class="fas fa-building"></i> Organization</h3>
            <dl class="summary-details">
              <dt>Name:</dt>
              <dd>{{ organizationData.name }}</dd>
              <dt>Industry:</dt>
              <dd>{{ getIndustryDisplay(organizationData.industry) }}</dd>
              <dt>Contact Email:</dt>
              <dd>{{ organizationData.primary_contact_email }}</dd>
              <dt>Subscription:</dt>
              <dd>{{ organizationData.subscription_tier }}</dd>
            </dl>
          </div>

          <div class="summary-section">
            <h3><i class="fas fa-th-large"></i> Template</h3>
            <dl class="summary-details">
              <dt>Template:</dt>
              <dd>{{ selectedTemplate?.name }}</dd>
              <dt>Projects:</dt>
              <dd>{{ selectedTemplate?.project_count }}</dd>
              <dt>Checks:</dt>
              <dd>{{ selectedTemplate?.check_count }}</dd>
            </dl>
          </div>

          <div class="summary-section">
            <h3><i class="fas fa-cogs"></i> Setup Options</h3>
            <ul class="setup-options-list">
              <li :class="{ enabled: setupOptions.auto_create_projects }">
                <i :class="setupOptions.auto_create_projects ? 'fas fa-check' : 'fas fa-times'"></i>
                Create {{ selectedTemplate?.project_count || 0 }} default projects
              </li>
              <li :class="{ enabled: setupOptions.auto_create_checks }">
                <i :class="setupOptions.auto_create_checks ? 'fas fa-check' : 'fas fa-times'"></i>
                Assign comprehensive checks
              </li>
              <li :class="{ enabled: setupOptions.auto_invite_users }">
                <i :class="setupOptions.auto_invite_users ? 'fas fa-check' : 'fas fa-times'"></i>
                Invite {{ setupOptions.default_users.length }} users
              </li>
            </ul>
          </div>

          <div v-if="setupOptions.auto_invite_users && setupOptions.default_users.length > 0" class="summary-section">
            <h3><i class="fas fa-users"></i> Users to Invite</h3>
            <ul class="users-list">
              <li v-for="user in setupOptions.default_users" :key="user.email">
                {{ user.first_name }} {{ user.last_name }} ({{ user.email }}) - {{ getRoleDisplay(user.role) }}
              </li>
            </ul>
          </div>
        </div>

        <div v-if="setupResults" class="setup-results">
          <div :class="['result-banner', setupResults.success ? 'success' : 'error']">
            <i :class="setupResults.success ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'"></i>
            <h3>{{ setupResults.success ? 'Setup Completed!' : 'Setup Failed' }}</h3>
            <p>{{ setupResults.message || setupResults.error }}</p>
          </div>

          <div v-if="setupResults.success" class="results-details">
            <div class="result-stat">
              <div class="stat-number">{{ setupResults.setup_results.projects_created }}</div>
              <div class="stat-label">Projects Created</div>
            </div>
            <div class="result-stat">
              <div class="stat-number">{{ setupResults.setup_results.checks_assigned }}</div>
              <div class="stat-label">Checks Assigned</div>
            </div>
            <div class="result-stat">
              <div class="stat-number">{{ setupResults.setup_results.users_created }}</div>
              <div class="stat-label">Users Created</div>
            </div>
            <div class="result-stat">
              <div class="stat-number">{{ setupResults.setup_results.clients_created }}</div>
              <div class="stat-label">Clients Created</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation Buttons -->
    <div class="wizard-navigation">
      <button
        @click="previousStep"
        class="btn btn-secondary"
        :disabled="currentStep === 0 || creating"
      >
        <i class="fas fa-arrow-left"></i> Previous
      </button>

      <div class="nav-spacer"></div>

      <button
        v-if="currentStep < steps.length - 1"
        @click="nextStep"
        class="btn btn-primary"
        :disabled="!canProceed || loadingTemplates"
      >
        Next <i class="fas fa-arrow-right"></i>
      </button>

      <button
        v-if="currentStep === steps.length - 1 && !setupResults"
        @click="createOrganization"
        class="btn btn-success"
        :disabled="creating || !canProceed"
      >
        <span v-if="creating" class="spinner"></span>
        {{ creating ? 'Creating Organization...' : 'Create Organization' }}
      </button>

      <button
        v-if="setupResults && setupResults.success"
        @click="$router.push(`/organizations/${setupResults.organization.id}`)"
        class="btn btn-primary"
      >
        View Organization <i class="fas fa-external-link-alt"></i>
      </button>
    </div>

    <!-- Notifications -->
    <div v-if="notification" class="notification" :class="notification.type">
      <i :class="getNotificationIcon(notification.type)"></i>
      {{ notification.message }}
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'OrganizationSetupWizard',
  data() {
    return {
      currentStep: 0,
      steps: [
        { id: 'org-details', label: 'Organization' },
        { id: 'template-selection', label: 'Template' },
        { id: 'customization', label: 'Customize' },
        { id: 'review-create', label: 'Create' }
      ],

      // Form data
      organizationData: {
        name: '',
        industry: '',
        primary_contact_email: '',
        subscription_tier: 'STANDARD',
        company_size: ''
      },

      // Templates
      templates: [],
      loadingTemplates: false,
      selectedTemplate: null,
      templatePreview: null,

      // Setup options
      setupOptions: {
        auto_create_projects: true,
        auto_create_checks: true,
        auto_invite_users: false,
        default_llm_provider: 'gemini',
        default_llm_model: 'gemini-1.5-pro',
        default_users: []
      },

      // State
      creating: false,
      setupResults: null,
      notification: null
    }
  },

  computed: {
    filteredTemplates() {
      if (!this.organizationData.industry) return this.templates
      return this.templates.filter(template => template.industry_type === this.organizationData.industry)
    },

    canProceed() {
      switch (this.currentStep) {
        case 0: // Organization details
          return this.organizationData.name &&
                 this.organizationData.industry &&
                 this.organizationData.primary_contact_email

        case 1: // Template selection
          return this.selectedTemplate !== null

        case 2: // Customization
          if (this.setupOptions.auto_invite_users) {
            return this.setupOptions.default_users.every(user =>
              user.email && user.first_name && user.last_name && user.role
            )
          }
          return true

        case 3: // Review & Create
          return true

        default:
          return false
      }
    }
  },

  async mounted() {
    await this.loadTemplates()
  },

  methods: {
    async loadTemplates() {
      this.loadingTemplates = true
      try {
        const response = await api.get('/document-review/templates/industry/')
        this.templates = response.data.templates
      } catch (error) {
        this.showNotification('error', 'Failed to load industry templates')
        console.error('Load templates error:', error)
      } finally {
        this.loadingTemplates = false
      }
    },

    async selectTemplate(template) {
      this.selectedTemplate = template

      // Load template preview
      try {
        const response = await api.get(`/document-review/templates/${template.id}/preview/`)
        this.templatePreview = response.data
      } catch (error) {
        this.showNotification('error', 'Failed to load template preview')
        console.error('Load preview error:', error)
      }
    },

    nextStep() {
      if (this.canProceed && this.currentStep < this.steps.length - 1) {
        this.currentStep++
      }
    },

    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--
      }
    },

    addUser() {
      this.setupOptions.default_users.push({
        role: '',
        email: '',
        first_name: '',
        last_name: ''
      })
    },

    removeUser(index) {
      this.setupOptions.default_users.splice(index, 1)
    },

    async createOrganization() {
      this.creating = true
      this.setupResults = null

      try {
        const payload = {
          organization: this.organizationData,
          template_id: this.selectedTemplate.id,
          setup_options: this.setupOptions
        }

        const response = await api.post('/document-review/organizations/create-with-template/', payload)

        this.setupResults = response.data
        this.showNotification('success', 'Organization created successfully!')

      } catch (error) {
        const errorData = error.response?.data || {}
        this.setupResults = {
          success: false,
          error: errorData.error || 'Failed to create organization',
          setup_results: errorData.setup_results || {}
        }
        this.showNotification('error', errorData.error || 'Failed to create organization')
        console.error('Create organization error:', error)
      } finally {
        this.creating = false
      }
    },

    getIndustryIcon(industry) {
      const icons = {
        'CONSTRUCTION': 'fas fa-hard-hat',
        'INSURANCE': 'fas fa-shield-alt',
        'REAL_ESTATE': 'fas fa-building',
        'HEALTHCARE': 'fas fa-hospital',
        'TECHNOLOGY': 'fas fa-laptop-code',
        'MANUFACTURING': 'fas fa-cogs',
        'RETAIL': 'fas fa-store',
        'PROFESSIONAL_SERVICES': 'fas fa-briefcase',
        'OTHER': 'fas fa-question-circle'
      }
      return icons[industry] || 'fas fa-building'
    },

    getIndustryDisplay(industry) {
      const displays = {
        'CONSTRUCTION': 'Construction',
        'INSURANCE': 'Insurance',
        'REAL_ESTATE': 'Real Estate',
        'HEALTHCARE': 'Healthcare',
        'TECHNOLOGY': 'Technology',
        'MANUFACTURING': 'Manufacturing',
        'RETAIL': 'Retail',
        'PROFESSIONAL_SERVICES': 'Professional Services',
        'OTHER': 'Other'
      }
      return displays[industry] || industry
    },

    getRoleDisplay(role) {
      const displays = {
        'ADMIN': 'Administrator',
        'RISK_MANAGER': 'Risk Manager',
        'ANALYST': 'Analyst',
        'CLIENT_MANAGER': 'Client Manager',
        'REVIEWER': 'Reviewer'
      }
      return displays[role] || role
    },

    showNotification(type, message) {
      this.notification = { type, message }
      setTimeout(() => {
        this.notification = null
      }, 5000)
    },

    getNotificationIcon(type) {
      const icons = {
        success: 'fas fa-check-circle',
        error: 'fas fa-exclamation-circle',
        warning: 'fas fa-exclamation-triangle',
        info: 'fas fa-info-circle'
      }
      return icons[type] || icons.info
    }
  }
}
</script>

<style scoped>
.organization-setup-wizard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

/* Header */
.wizard-header {
  text-align: center;
  margin-bottom: 40px;
}

.wizard-header h1 {
  color: #2c3e50;
  font-size: 2.5rem;
  margin-bottom: 10px;
}

.wizard-subtitle {
  color: #7f8c8d;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
}

/* Progress Indicator */
.progress-indicator {
  margin-bottom: 40px;
}

.progress-steps {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 20px;
  opacity: 0.5;
}

.progress-step.active {
  opacity: 1;
}

.progress-step.completed {
  opacity: 0.8;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-weight: bold;
  color: #6c757d;
  transition: all 0.3s;
}

.progress-step.active .step-circle {
  background: #667eea;
  color: white;
}

.progress-step.completed .step-circle {
  background: #28a745;
  color: white;
}

.step-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2c3e50;
  text-align: center;
}

.progress-bar {
  height: 4px;
  background: #e9ecef;
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  transition: width 0.3s ease;
}

/* Step Content */
.wizard-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  padding: 40px;
  margin-bottom: 30px;
  min-height: 500px;
}

.step-header {
  text-align: center;
  margin-bottom: 30px;
}

.step-header h2 {
  color: #2c3e50;
  font-size: 1.8rem;
  margin-bottom: 8px;
}

.step-header p {
  color: #7f8c8d;
  font-size: 1rem;
}

/* Forms */
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #2c3e50;
}

.form-control {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Template Selection */
.template-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.template-card {
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
}

.template-card:hover {
  border-color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
}

.template-card.selected {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05));
}

.template-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.template-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  margin-right: 16px;
}

.template-meta h3 {
  margin: 0 0 4px 0;
  color: #2c3e50;
  font-size: 1.2rem;
}

.industry-badge {
  background: #e9ecef;
  color: #6c757d;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.template-description {
  color: #7f8c8d;
  margin-bottom: 16px;
  line-height: 1.5;
}

.template-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.stat {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6c757d;
  font-size: 0.9rem;
}

.default-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #28a745;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Template Preview */
.template-preview {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 24px;
  margin-top: 20px;
}

.template-preview h3 {
  color: #2c3e50;
  margin-bottom: 20px;
}

.preview-section {
  margin-bottom: 24px;
}

.preview-section h4 {
  color: #495057;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.preview-list {
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.preview-item {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f3f4;
}

.preview-item:last-child {
  border-bottom: none;
}

.preview-item strong {
  color: #2c3e50;
  display: block;
  margin-bottom: 4px;
}

.preview-item p {
  color: #7f8c8d;
  margin: 4px 0;
  font-size: 0.9rem;
}

.preview-item small {
  color: #6c757d;
  font-size: 0.8rem;
}

/* Customization Options */
.customization-options {
  max-width: 800px;
  margin: 0 auto;
}

.option-group {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
}

.option-group h3 {
  color: #2c3e50;
  margin-bottom: 16px;
}

.option-item {
  margin-bottom: 16px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  padding: 12px;
  border-radius: 6px;
  transition: background 0.3s;
}

.checkbox-label:hover {
  background: rgba(102, 126, 234, 0.05);
}

.checkbox {
  margin-right: 12px;
  margin-top: 2px;
  width: 18px;
  height: 18px;
}

.checkbox-content strong {
  color: #2c3e50;
  display: block;
  margin-bottom: 4px;
}

.checkbox-content p {
  color: #7f8c8d;
  margin: 0;
  font-size: 0.9rem;
}

.warning-text {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 4px;
}

/* User Input */
.user-input-row {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}

.user-input-row .form-control {
  flex: 1;
}

.role-select {
  min-width: 180px;
}

.btn-add-user, .btn-remove-user {
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-add-user {
  background: #28a745;
  color: white;
}

.btn-add-user:hover {
  background: #218838;
}

.btn-remove-user {
  background: #dc3545;
  color: white;
}

.btn-remove-user:hover {
  background: #c82333;
}

/* Review Summary */
.review-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 30px;
}

.summary-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
}

.summary-section h3 {
  color: #2c3e50;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-details {
  margin: 0;
}

.summary-details dt {
  font-weight: 600;
  color: #495057;
  margin-bottom: 4px;
}

.summary-details dd {
  color: #7f8c8d;
  margin-bottom: 12px;
  margin-left: 0;
}

.setup-options-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.setup-options-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  color: #6c757d;
}

.setup-options-list li.enabled {
  color: #28a745;
}

.setup-options-list li.enabled i {
  color: #28a745;
}

.users-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.users-list li {
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
  color: #495057;
}

.users-list li:last-child {
  border-bottom: none;
}

/* Setup Results */
.setup-results {
  margin-top: 30px;
}

.result-banner {
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.result-banner.success {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.result-banner.error {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.result-banner h3 {
  margin: 0;
  font-size: 1.2rem;
}

.result-banner p {
  margin: 4px 0 0 0;
}

.results-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
  text-align: center;
}

.result-stat {
  background: white;
  border-radius: 8px;
  padding: 20px;
  border: 2px solid #e9ecef;
}

.stat-number {
  font-size: 2rem;
  font-weight: bold;
  color: #28a745;
  margin-bottom: 4px;
}

.stat-label {
  color: #6c757d;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Navigation */
.wizard-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-spacer {
  flex: 1;
}

/* Buttons */
.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5a6fd8;
  transform: translateY(-1px);
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #218838;
  transform: translateY(-1px);
}

/* States */
.loading-state, .empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
}

.loading-state i, .empty-state i {
  font-size: 3rem;
  margin-bottom: 16px;
  display: block;
}

.empty-state h3 {
  color: #495057;
  margin-bottom: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Notifications */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 16px 20px;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  z-index: 1001;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

.notification.success { background: #28a745; }
.notification.error { background: #dc3545; }
.notification.warning { background: #ffc107; color: #212529; }
.notification.info { background: #17a2b8; }

/* Responsive */
@media (max-width: 768px) {
  .organization-setup-wizard {
    padding: 15px;
  }

  .wizard-header h1 {
    font-size: 2rem;
  }

  .wizard-content {
    padding: 20px;
  }

  .progress-steps {
    flex-wrap: wrap;
    gap: 10px;
  }

  .progress-step {
    margin: 0;
    min-width: 80px;
  }

  .template-grid {
    grid-template-columns: 1fr;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .review-summary {
    grid-template-columns: 1fr;
  }

  .wizard-navigation {
    flex-direction: column;
    gap: 16px;
  }

  .user-input-row {
    flex-direction: column;
    align-items: stretch;
  }

  .results-details {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
